import { spawn } from 'node:child_process'
import fs from 'node:fs'
import path from 'node:path'

const LOCAL_PROTOCOL_PATTERN = /^(workspace|link|file|portal):/
const ADMIN_CONFIG_IMPORT_PATTERN =
  /^import\s+\{[^}]*\bdefineConfig\b[^}]*\}\s+from\s+['"]betterstart-cli['"];?[^\S\n]*$/m
const ADMIN_CONFIG_IMPORT_SHIM = 'const defineConfig = <T>(config: T): T => config'
const LOCKFILE_REFRESH_TIMEOUT_MS = 120_000

export type LockfileGuardStatus = 'updated' | 'failed' | 'not-needed'
export type LockfileRefresher = (cwd: string) => Promise<boolean>

export interface ProjectDeployGuard {
  localSpecDeps: string[]
  lockfile: LockfileGuardStatus
  restore(): void
}

/**
 * Temporarily remove CLI-only/local dependency state that cannot install on a
 * remote build machine. The original package.json, lockfile, and admin config
 * are restored byte-for-byte after the provider finishes uploading the source.
 */
export async function guardProjectForDeploy(
  cwd: string,
  refreshLockfile: LockfileRefresher = runPnpmLockfileOnly
): Promise<ProjectDeployGuard> {
  const restores: Array<() => void> = []
  const { localSpecDeps, removedCliDep } = guardPackageJson(cwd, restores)
  guardAdminConfig(cwd, restores)
  const lockfile = removedCliDep
    ? await guardPnpmLockfile(cwd, restores, refreshLockfile)
    : 'not-needed'

  return {
    localSpecDeps,
    lockfile,
    restore() {
      for (const restoreFile of restores) {
        try {
          restoreFile()
        } catch {
          // A deploy result must not be replaced by a best-effort restore error.
        }
      }
    }
  }
}

function guardPackageJson(
  cwd: string,
  restores: Array<() => void>
): { localSpecDeps: string[]; removedCliDep: boolean } {
  const packageJsonPath = path.join(cwd, 'package.json')
  if (!fs.existsSync(packageJsonPath)) {
    return { localSpecDeps: [], removedCliDep: false }
  }

  const original = fs.readFileSync(packageJsonPath, 'utf-8')
  let parsed: Record<string, unknown>
  try {
    parsed = JSON.parse(original) as Record<string, unknown>
  } catch {
    return { localSpecDeps: [], removedCliDep: false }
  }

  const localSpecDeps: string[] = []
  let removedCliDep = false

  for (const section of ['dependencies', 'devDependencies'] as const) {
    const deps = parsed[section]
    if (!deps || typeof deps !== 'object') continue
    const record = deps as Record<string, unknown>

    if ('betterstart-cli' in record) {
      Reflect.deleteProperty(record, 'betterstart-cli')
      removedCliDep = true
    }

    for (const [name, spec] of Object.entries(record)) {
      if (typeof spec === 'string' && LOCAL_PROTOCOL_PATTERN.test(spec)) {
        localSpecDeps.push(name)
      }
    }
  }

  if (removedCliDep) {
    fs.writeFileSync(packageJsonPath, `${JSON.stringify(parsed, null, 2)}\n`, 'utf-8')
    restores.push(() => fs.writeFileSync(packageJsonPath, original, 'utf-8'))
  }

  return { localSpecDeps, removedCliDep }
}

async function guardPnpmLockfile(
  cwd: string,
  restores: Array<() => void>,
  refreshLockfile: LockfileRefresher
): Promise<LockfileGuardStatus> {
  const lockfilePath = path.join(cwd, 'pnpm-lock.yaml')
  if (!fs.existsSync(lockfilePath)) return 'not-needed'

  const original = fs.readFileSync(lockfilePath, 'utf-8')
  restores.push(() => fs.writeFileSync(lockfilePath, original, 'utf-8'))
  return (await refreshLockfile(cwd)) ? 'updated' : 'failed'
}

function runPnpmLockfileOnly(cwd: string): Promise<boolean> {
  return new Promise((resolve) => {
    const child = spawn('pnpm', ['install', '--lockfile-only'], { cwd, stdio: 'ignore' })
    const timer = setTimeout(() => {
      child.kill('SIGKILL')
      resolve(false)
    }, LOCKFILE_REFRESH_TIMEOUT_MS)
    child.on('close', (code) => {
      clearTimeout(timer)
      resolve(code === 0)
    })
    child.on('error', () => {
      clearTimeout(timer)
      resolve(false)
    })
  })
}

function guardAdminConfig(cwd: string, restores: Array<() => void>): void {
  const adminConfigPath = path.join(cwd, 'admin.config.ts')
  if (!fs.existsSync(adminConfigPath)) return

  const original = fs.readFileSync(adminConfigPath, 'utf-8')
  if (!ADMIN_CONFIG_IMPORT_PATTERN.test(original)) return

  fs.writeFileSync(
    adminConfigPath,
    original.replace(ADMIN_CONFIG_IMPORT_PATTERN, ADMIN_CONFIG_IMPORT_SHIM),
    'utf-8'
  )
  restores.push(() => fs.writeFileSync(adminConfigPath, original, 'utf-8'))
}
